import type { ApiClient } from '../api/client.js';

export type ChatPushState = 'unsupported' | 'unconfigured' | 'denied' | 'off' | 'on';

type PushApi = Pick<
  ApiClient,
  'getDirectChatPushConfig' | 'subscribeDirectChatPush' | 'unsubscribeDirectChatPush'
>;

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=');
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (
    (await navigator.serviceWorker.getRegistration('/')) ??
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
  );
}

export async function getChatPushState(api: PushApi): Promise<ChatPushState> {
  if (!pushSupported()) return 'unsupported';
  const config = await api.getDirectChatPushConfig();
  if (!config.enabled) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';
  const existing = await (
    await navigator.serviceWorker.getRegistration('/')
  )?.pushManager.getSubscription();
  return existing && Notification.permission === 'granted' ? 'on' : 'off';
}

export async function enableChatPush(api: PushApi): Promise<ChatPushState> {
  const config = await api.getDirectChatPushConfig();
  if (!config.enabled) return 'unconfigured';
  if ((await Notification.requestPermission()) !== 'granted') {
    return Notification.permission === 'denied' ? 'denied' : 'off';
  }
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const subscription =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(config.publicKey),
    }));
  await api.subscribeDirectChatPush(subscription.toJSON());
  return 'on';
}

export async function disableChatPush(api: PushApi): Promise<ChatPushState> {
  const subscription = await (
    await navigator.serviceWorker.getRegistration('/')
  )?.pushManager.getSubscription();
  if (subscription) {
    await api.unsubscribeDirectChatPush(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  }
  return 'off';
}
